import { useNavigate, useParams } from "@tanstack/solid-router";
import { createMutation, createQuery, useQueryClient } from "@tanstack/solid-query";
import { Html5Qrcode } from "html5-qrcode";
import { For, Show, createEffect, createSignal, onCleanup } from "solid-js";
import { Avatar } from "~/components/Avatar";
import { EventPageHeader } from "~/components/EventPageHeader";
import { SponsorLeadCapture } from "~/components/SponsorLeadCapture";
import { TicketClaimGate } from "~/components/TicketClaimGate";
import {
  fetchConnectEvents,
  fetchProfile,
  fetchScanQuota,
  hasClaimedTicket,
  scanBadge,
  type BadgeScanResult,
  type FollowUpAction,
} from "~/lib/api";
import { track } from "~/lib/analytics";
import { parseTicketId } from "~/lib/scan";

const SCANNER_ID = "badge-scan-reader";

export function BadgeScanPage() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const eventId = () => params().eventId ?? "";

  const [result, setResult] = createSignal<BadgeScanResult | null>(null);
  const [scanError, setScanError] = createSignal("");
  const [manualId, setManualId] = createSignal("");
  const [cameraOn, setCameraOn] = createSignal(false);
  const [cameraError, setCameraError] = createSignal("");
  const [cameraWanted, setCameraWanted] = createSignal(true);
  let scanner: Html5Qrcode | null = null;
  let handling = false;

  const events = createQuery(() => ({ queryKey: ["events"], queryFn: fetchConnectEvents }));
  const profileQuery = createQuery(() => ({
    queryKey: ["profile", eventId()],
    queryFn: () => fetchProfile(eventId()),
    enabled: !!eventId(),
  }));
  const quotaQuery = createQuery(() => ({
    queryKey: ["scan-quota", eventId()],
    queryFn: () => fetchScanQuota(eventId()),
    enabled: !!eventId() && hasClaimedTicket(profileQuery.data?.data),
  }));

  const needsTicket = () =>
    profileQuery.isSuccess && !hasClaimedTicket(profileQuery.data?.data ?? null);

  const scanMutation = createMutation(() => ({
    mutationFn: (ticketId: string) => scanBadge(eventId(), ticketId),
    onSuccess: (res) => {
      setResult(res.data);
      setScanError("");
      setCameraWanted(false);
      void stopCamera();
      queryClient.invalidateQueries({ queryKey: ["scan-quota", eventId()] });
      queryClient.invalidateQueries({ queryKey: ["connections", eventId()] });
    },
    onError: (err: unknown) => {
      setResult(null);
      setScanError((err as Error).message);
      setCameraWanted(false);
      void stopCamera();
    },
  }));

  function readerReady(): boolean {
    return typeof document !== "undefined" && document.getElementById(SCANNER_ID) != null;
  }

  async function stopCamera() {
    if (!scanner) return;
    try {
      if (scanner.isScanning) {
        await scanner.stop();
      }
      await scanner.clear();
    } catch {
      // ignore teardown errors
    }
    setCameraOn(false);
  }

  async function startCamera() {
    if (!cameraWanted() || result() || scanMutation.isPending) return;
    if (!readerReady()) return;

    setCameraError("");
    if (!scanner) {
      scanner = new Html5Qrcode(SCANNER_ID);
    }
    if (scanner.isScanning) return;

    try {
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decoded) => void onDecoded(decoded),
        () => {},
      );
      setCameraOn(true);
      track("scan_started", undefined, { ticketingEventId: eventId() });
    } catch (err) {
      setCameraError(err instanceof Error ? err.message : "Could not open camera");
      setCameraOn(false);
    }
  }

  async function onDecoded(raw: string) {
    if (handling || scanMutation.isPending) return;
    const ticketId = parseTicketId(raw);
    if (!ticketId) return;
    handling = true;
    setCameraWanted(false);
    await stopCamera();
    scanMutation.mutate(ticketId);
    handling = false;
  }

  function submitManual() {
    const ticketId = parseTicketId(manualId());
    if (!ticketId) return;
    setCameraWanted(false);
    void stopCamera();
    scanMutation.mutate(ticketId);
  }

  function scanAgain() {
    setResult(null);
    setScanError("");
    setCameraWanted(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => void startCamera());
    });
  }

  createEffect(() => {
    if (!cameraWanted() || result() || scanMutation.isPending || !eventId()) return;
    requestAnimationFrame(() => void startCamera());
  });

  onCleanup(() => {
    setCameraWanted(false);
    void stopCamera();
    scanner = null;
  });

  const eventTitle = () => {
    const match = events.data?.data.find((e) => e.ticketing_event_id === eventId());
    if (!match) return eventId();
    if (match.city) return `${match.city} 2026`;
    return match.name;
  };

  const quotaLabel = () => {
    const q = quotaQuery.data?.data;
    if (!q) return null;
    if (q.is_sponsor) return "Sponsor · unlimited scans";
    if (q.scans_remaining != null) return `${q.scans_remaining} scans left`;
    return null;
  };

  const showScanner = () => cameraWanted() && !result() && !scanMutation.isPending;

  return (
    <section class="page page--flush scan-page">
      <Show when={needsTicket()}>
        <div style={{ padding: "20px" }}>
          <TicketClaimGate eventId={eventId()} cityLabel={eventTitle()} />
        </div>
      </Show>

      <Show when={!needsTicket()}>
      <EventPageHeader
        eventTitle={eventTitle()}
        label="Scan badge"
        trailing={
          <div class="scan-header-trailing">
            <Show when={quotaQuery.data?.data.is_sponsor}>
              <button
                type="button"
                class="btn btn-ghost btn-sm"
                onClick={() =>
                  navigate({ to: "/directory/$eventId/leads", params: { eventId: eventId() } })
                }
              >
                Leads
              </button>
            </Show>
            <Show when={quotaLabel()} keyed>
              {(label) => <span class="status-chip">{label}</span>}
            </Show>
          </div>
        }
      />

      {/* Keep reader mounted — html5-qrcode requires a stable DOM node */}
      <div
        id={SCANNER_ID}
        class="scan-reader"
        classList={{ "scan-reader--hidden": !showScanner() }}
        aria-hidden={!showScanner()}
      />

      <Show when={showScanner()}>
        <Show when={cameraError()}>
          <div class="error-box scan-error">{cameraError()}</div>
        </Show>
        <Show when={!cameraOn() && !cameraError()}>
          <p class="muted scan-hint">Starting camera…</p>
        </Show>
        <p class="muted scan-hint">Point at the QR on an attendee lanyard.</p>

        <div class="scan-manual">
          <label class="field">
            <span>Or paste ticket ID (dev / fallback)</span>
            <input
              value={manualId()}
              onInput={(e) => setManualId(e.currentTarget.value)}
              placeholder="Ticket Tailor ticket id"
            />
          </label>
          <button type="button" class="btn btn-outline" onClick={submitManual}>
            Look up
          </button>
        </div>
      </Show>

      <Show when={scanMutation.isPending}>
        <p class="muted scan-hint">Looking up badge…</p>
      </Show>

      <Show when={scanError()}>
        <div class="error-box">{scanError()}</div>
        <button type="button" class="btn btn-primary" onClick={scanAgain}>
          Try again
        </button>
      </Show>

      <Show when={result()} keyed>
        {(res) => {
          const d = res.display;
          return (
            <div class="scan-result card">
              <div class="profile-detail-header">
                <Avatar url={d.avatar_url} name={d.display_name} size="lg" />
                <div>
                  <h1>{d.display_name ?? "Attendee"}</h1>
                  <p class="profile-preview-headline">{d.headline ?? d.job_role}</p>
                  <p class="muted">{d.company}</p>
                </div>
              </div>

              <Show when={res.flags.ask_to_join_connect}>
                <p class="scan-nudge">Hasn't joined the Connect directory yet — ask them to open Connect and opt in.</p>
              </Show>

              <Show when={d.bio}>
                <p class="profile-detail-bio">{d.bio}</p>
              </Show>
              <Show when={d.looking_for}>
                <p class="looking-for">
                  <span class="status-chip">Looking for</span> {d.looking_for}
                </p>
              </Show>

              <Show when={d.interests?.length}>
                <div class="interest-grid">
                  <For each={d.interests!}>
                    {(tag) => <span class="interest-chip selected">{tag.label}</span>}
                  </For>
                </div>
              </Show>

              <Show when={res.contact.email}>
                <p class="muted">
                  Contact: <a href={`mailto:${res.contact.email}`}>{res.contact.email}</a>
                </p>
              </Show>

              <Show when={!res.contact.email && res.contact.ticket_email_redacted}>
                <p class="muted">Ticket email: {res.contact.ticket_email_redacted}</p>
              </Show>

              <Show when={res.connection}>
                <p class="status-chip">
                  {res.connection!.created ? "Added to your connections" : "Already connected"}
                </p>
              </Show>

              <Show when={res.can_manage_lead}>
                <SponsorLeadCapture
                  scanId={res.scan_id}
                  initialNote={res.sponsor_lead?.note ?? ""}
                  initialRating={res.sponsor_lead?.rating ?? null}
                  initialFollowUp={(res.sponsor_lead?.follow_up as FollowUpAction | null) ?? null}
                />
              </Show>

              <div class="wizard-actions">
                <button
                  type="button"
                  class="btn btn-ghost"
                  onClick={() => navigate({ to: "/directory/$eventId", params: { eventId: eventId() } })}
                >
                  Directory
                </button>
                <Show when={res.user_id} keyed>
                  {(userId) => (
                    <button
                      type="button"
                      class="btn btn-outline"
                      onClick={() =>
                        navigate({
                          to: "/directory/$eventId/profile/$profileId",
                          params: { eventId: eventId(), profileId: userId },
                        })
                      }
                    >
                      View profile
                    </button>
                  )}
                </Show>
                <button type="button" class="btn btn-primary" onClick={scanAgain}>
                  Scan another
                </button>
              </div>
            </div>
          );
        }}
      </Show>
      </Show>
    </section>
  );
}